import {
  AUTH_LOGIN_URL,
  AUTH_LOGOUT_URL,
  AUTH_RECOVERY_PASSWORD_CONFIRM_URL,
  AUTH_RECOVERY_PASSWORD_URL,
  SIGNUP_CHECK_USER_EXISTS_URL,
  SIGNUP_CONFIRM_CODE_URL,
  SIGNUP_RESEND_CODE_URL,
  SIGNUP_URL,
} from '@/constants/constants';
import { JwtPayload, jwtDecode } from 'jwt-decode';

import { handleRequestError, useRestClient } from './useRestClient';

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/AuthStore';
import { UserDTO } from '../DTOs/UserDTO';

/**
 * Serviço para gerenciamento de usuários.
 *
 * Este arquivo contém todas as funções relacionadas às operações de usuários,
 * como cadastro, edição e exclusão. Qualquer novo serviço relacionado ao usuário
 * deve ser adicionado aqui.
 */

export const useUserService = () => {
  const { restClient } = useRestClient();
  const { login, updateTokens, getToken, logout } = useAuthStore();

  /**
   * Realiza o cadastro de um novo usuário.
   *
   * @param {object} user - Dados do usuário para cadastro.
   * @returns {Promise<string>} Mensagem de sucesso retornada pela API.
   * @throws {Error} Caso ocorra um erro na requisição.
   */
  const signUp = async (user: UserDTO): Promise<string> => {
    try {
      const { data } = await restClient.post(SIGNUP_URL, {
        ...user,
      });
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  /**
   * Realiza o login de um usuário.
   *
   * @param {string} email - O e-mail do usuário para autenticação.
   * @param {string} password - A senha do usuário para autenticação.
   * @returns {Promise<void>} Retorna uma Promise sem valor, se o login for bem-sucedido.
   * @throws {Error} Caso ocorra um erro na requisição.
   */
  const signIn = async (
    email: string,
    password: string,
    language: string
  ): Promise<void> => {
    try {
      const { data } = await restClient.post(AUTH_LOGIN_URL, {
        email,
        password,
        language,
      });

      if (data.id_token && data.access_token && data.refresh_token) {
        const dataTokens = {
          id_token: data.id_token,
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expiresAt: data.expiresAt.toString(),
          expiresIn: data.expiresIn,
        };

        const decodedToken = jwtDecode<
          JwtPayload & {
            name: string;
            family_name: string;
            email: string;
            picture: string;
            birthdate: string;
          }
        >(data.id_token);

        const userData: UserDTO = {
          id: decodedToken.sub || '',
          name: decodedToken.name,
          surname: decodedToken.family_name,
          email: decodedToken.email,
          picture: decodedToken.picture,
          birthDate: decodedToken.birthdate,
          phone: '',
          zip: '',
          address: '',
          complement: undefined,
          password: '',
          city: undefined,
          state: undefined,
          gender: '',
          countryCode:''
        };

        // Atualiza o estado global do usuário e do token
        login(
          {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            surname: userData.surname,
            picture: userData.picture,
            birthdate: userData.birthDate,
          },
          {
            id_token: data.id_token,
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expiresAt: data.expiresAt,
            expiresIn: data.expiresIn,
          }
        );

        // Armazena o `access_token` e o `refresh_token` no Zustand/localStorage
        updateTokens(dataTokens);
      }
    } catch (error) {
      throw handleRequestError(error);
    }
  };
  const signOut = async (): Promise<void> => {
    try {
      const tokens = getToken();

      if (!tokens || !tokens.refresh_token) {
        throw new Error('Refresh token não encontrado.');
      }

      await restClient.post(
        AUTH_LOGOUT_URL,
        { refreshToken: tokens.refresh_token },
        {
          headers: {
            Authorization: `Bearer ${tokens.id_token}`,
          },
        }
      );
      logout();
    } catch (error) {
      throw handleRequestError(error);
    }
  };


  const recoveryPassword = async (email: string) => {
    try {
      const { data } = await restClient.put(AUTH_RECOVERY_PASSWORD_URL, {
        email,
      });
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const registerNewPassword = async (
    email: string,
    code: string,
    password: string
  ) => {
    try {
      const { data } = await restClient.put(
        AUTH_RECOVERY_PASSWORD_CONFIRM_URL,
        {
          email,
          code,
          password,
        }
      );
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const confirmSignUpCode = async (
    email: string,
    password: string,
    code: string,
    language: string
  ) => {
    try {
      const { data } = await restClient.put(SIGNUP_CONFIRM_CODE_URL, {
        email,
        password,
        code,
        language,
      });
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const resendVerificationCode = async (email: string) => {
    try {
      const { data } = await restClient.put(SIGNUP_RESEND_CODE_URL, {
        email,
      });
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const checkEmailAlreadyExist = async (email: string) => {
    try {
      const { data } = await restClient.get(
        `${SIGNUP_CHECK_USER_EXISTS_URL}/${email}`
      );
      return data;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  return useMemo(
    () => ({
      signUp,
      signIn,
      signOut,
      recoveryPassword,
      registerNewPassword,
      resendVerificationCode,
      checkEmailAlreadyExist,
      confirmSignUpCode,
    }),
    []
  );
};
